import { swSettings } from "app/common/services/swSettings";
import { DefaultCell, GroupTrafficShare, IndexCell, TrafficShare } from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter } from "app/filters/i18nFilter";
import queryString from "query-string";
import type { FC } from "react";
import { useCallback } from "react";
import { AdNetworksTableTop } from "app/pages/website-analysis/traffic-sources/display-ads/ad-networks/AdNetworksTableTop";
import { apiHelper } from "app/common/services/apiHelper";
import type { IAdNetworksTableProps } from "app/pages/website-analysis/traffic-sources/display-ads/ad-networks/AdNetworksContainer";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { allTrackers } from "app/services/track/track";

export const AdNetworksTableCompare: FC<IAdNetworksTableProps> = (props) => {
    const { i18n, applyUpdateParams } = props;
    const { webSource, sort, adNetwork } = props.params;

    let field = "TotalShare";
    let sortDirection = "desc";
    if (sort) {
        [field, sortDirection] = sort.split(" ");
    }
    const columns = [
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 46,
            isResizable: false,
        },
        {
            field: "Name",
            fixed: true,
            displayName: i18nFilter()("analysis.source.ad.networks.table.columns.network.title"),
            sortable: true,
            cellComponent: DefaultCell,
            showTotalCount: true,
            width: 400,
        },
        {
            field: "TotalShare",
            displayName: i18n("analysis.source.search.all.table.columns.totalShareCompare.title"),
            tooltip: i18n("analysis.source.search.all.table.columns.totalShareCompare.fix.title.tooltip"),
            cellComponent: TrafficShare,
            sortable: true,
            minWidth: 190,
            maxWidth: 400,
        },
        {
            field: "SiteOrigins",
            displayName: i18n("analysis.source.search.all.table.columns.shareCompare.title"),
            cellComponent: GroupTrafficShare,
            minWidth: 250,
            isResizable: false,
        },
    ].map((col: any) => {
        const isSorted = col.field === field;
        return {
            ...col,
            visible: col.visible !== false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection,
            isResizable: col.isResizable !== false,
        };
    });

    const onSort = ({ field, sortDirection }) => {
        applyUpdateParams({
            sort: `${field} ${sortDirection}`,
        });
    };

    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const getExcelUrl = () => {
        const queryStringParams = queryString.stringify(getInitialFilters(props.params));
        return `/export/analysis/GetTrafficDisplayAdvertisingAdsTsv?${queryStringParams}`;
    };

    const a2d = useCallback(() => {
        return (
            <AddToDashboard
                webSource={webSource}
                type="Table"
                metric="DashboardAdNetworks"
                modelType="fromWebsite"
            />
        );
    }, [webSource]);

    const onSearch = (value) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value || ""}`);

        applyUpdateParams({
            adNetwork: value,
        });
    };

    const getInitialFilters = (params) => {
        const transformedParams = { ...params };
        // orderBy
        if (transformedParams.sort) {
            transformedParams.orderby = transformedParams.sort;
        } else {
            transformedParams.orderby = "TotalShare desc";
        }

        const filters = [];
        if (transformedParams.adNetwork) {
            filters.push(`Name;contains;"${decodeURIComponent(transformedParams.adNetwork)}"`);
            delete transformedParams.adNetwork;
        }
        if (filters.length > 0) {
            transformedParams.filter = filters.join(",");
        }

        return apiHelper.transformParamsForAPI(transformedParams);
    };

    return (
        <div data-automation="ad-networks-compare">
            <SWReactTableWrapper
                tableOptions={{
                    metric: "TrafficSourcesDisplayNetworks",
                    defaultSorted: "TotalShare",
                }}
                serverApi={`/api/websiteanalysis/GetTrafficDisplayAdvertisingAdsTable`}
                tableColumns={columns}
                initialFilters={getInitialFilters(props.params)}
                recordsField="Records"
                totalRecordsField="TotalCount"
                onSort={onSort}
                preventDefaultOnSort
                pageIndent={1}
            >
                {(topComponentProps) => (
                    <AdNetworksTableTop
                        downloadExcelPermitted={downloadExcelPermitted}
                        excelLink={getExcelUrl()}
                        a2d={a2d}
                        searchValue={adNetwork}
                        onChange={onSearch}
                        isCompare={true}
                        i18n={i18n}
                        {...topComponentProps}
                    />
                )}
            </SWReactTableWrapper>
        </div>
    );
};
